import { DateTime } from "luxon";
import { SalesReportSettingsType } from "../SalesReport";

export class SalesReportDate {
    private _date: DateTime;
    private _reportType: SalesReportSettingsType;
    private _offset: number = 0;
    private _language: string = navigator.language;
    private _customStart?: DateTime;
    private _customEnd?: DateTime;

    public constructor(d: DateTime, reportType: SalesReportSettingsType, offset: number = 0, language: string = navigator.language) {
        this._date = d;
        this._reportType = reportType;
        this._offset = offset;
        this._language = language;
    }

    set language(language: string) {
        this._language = language;
    }

    get language() {
        return this._language;
    }

    set date(d: DateTime) {
        this._date = d;
    }

    get date() {
        return this._date;
    }

    set reportType(reportType: SalesReportSettingsType) {
        this._reportType = reportType;
    }

    set customStart(d: DateTime | undefined) {
        this._customStart = d;
    }

    set customEnd(d: DateTime | undefined) {
        this._customEnd = d;
    }

    set offset(offset: number) {
        this._offset = offset;
    }

    get startDate() {
        let start: DateTime;

        start = this.centerTime;
        if (start.hour < 4) {
            start = start.minus({ days: 1 });
        }

        switch (this._reportType) {
            case 'daily':
                start = start.set({ hour: 4, minute: 0, second: 0, millisecond: 0 });
                break;
            case 'weekly':
                start = start.startOf('week', {
                    useLocaleWeeks: false
                }).set({ hour: 4, minute: 0, second: 0, millisecond: 0 });
                break;
            case 'monthly':
                start = start.startOf('month').set({ hour: 4, minute: 0, second: 0, millisecond: 0 });
                break;
            case 'yearly':
                start = start.startOf('year').set({ hour: 4, minute: 0, second: 0, millisecond: 0 });
                break;
            case 'custom':
                if (this._customStart) {
                    return this._customStart.set({ hour: 4, minute: 0, second: 0, millisecond: 0 });
                }
                start = start.set({ hour: 4, minute: 0, second: 0, millisecond: 0 });
                break;
            default:
                start = start.set({ hour: 4, minute: 0, second: 0, millisecond: 0 });
        }

        return start
    }

    get endDate() {
        let end: DateTime;
        end = this.centerTime
        if (end.hour < 4) {
            end = end.minus({ days: 1 });
        }
        
        switch (this._reportType) {
            case 'daily':
                end = end.plus({ days: 1 }).set({ hour: 3, minute: 59, second: 59, millisecond: 999 });
                break;
            case 'weekly':
                end = end.endOf('week').plus({ days: 1 }).set({ hour: 3, minute: 59, second: 59, millisecond: 999 });
                break;
            case 'monthly':
                end = end.endOf('month').plus({ days: 1 }).set({ hour: 3, minute: 59, second: 59, millisecond: 999 });
                break;
            case 'yearly':
                end = end.endOf('year').plus({ days: 1 }).set({ hour: 3, minute: 59, second: 59, millisecond: 999 });
                break;
            case 'custom':
                if (this._customEnd) {
                    return this._customEnd.plus({ days: 1 }).set({ hour: 3, minute: 59, second: 59, millisecond: 999 });
                }
                end = end.plus({ days: 1 }).set({ hour: 3, minute: 59, second: 59, millisecond: 999 });
                break;
            default:
                end = end.plus({ days: 1 }).set({ hour: 3, minute: 59, second: 59, millisecond: 999 });
        }

        return end
    }

    get centerTime() {
        let utcString = this.getUtcString();
        let date = this._date.setZone(utcString);
        return date;
    }

    private getUtcString(): string {
        if (this._offset >= 0) {
            return `UTC+${this._offset}`
        } else if (this._offset < 0) {
            return `UTC${this._offset}`
        } else {
            return `UTC${this._offset}`
        }
    }

    get datesBoundaries() {
        return {
            start: this.startDate.toJSDate(),
            end: this.endDate.toJSDate()
        }
    }

    get datesInRange() {
        switch (this._reportType) {
            case 'daily':
                return this.getHourlyRange();
            case 'weekly':
                return this.getWeeklyRange();
            case 'monthly':
                return this.getMonthlyRange();
            case 'yearly':
                return this.getYearlyRange();
            case 'custom':
                return this.getCustomRange();
            default:
                return this.getHourlyRange();
        }
    }

    getHourlyRange(): string[] {
        // Start hours from 4 am to 3 am next day
        // create an array of hours from 4 to 3
        const startHour = 4;
        // if current center time is before 4am, remove a day
        if (this.centerTime.hour < 4) {
            this._date = this._date.minus({ days: 1 });
        }

        const hours: string[] = [];
        // let d = this.centerTime.set({ hour: startHour, minute: 0, second: 0, millisecond: 0 });
        // copy the date to avoid mutating the original date
        let d = this.centerTime;

        for (let i = 0; i < 24; i++) {
            let h = i + startHour;
            if (h >= 24) {
                h = h - 24;
            }

            let fullDate = d.toJSDate();
            fullDate.setHours(h);

            // hours.push(d.set({ hour: i, minute: 0, second: 0, millisecond: 0 }).toFormat('HH'));
            hours.push(new Intl.DateTimeFormat(this.language, {
                hour: '2-digit',
            }).format(fullDate))

        }
        return hours
    }

    getWeeklyRange(): string[] {
        const startDate = this.startDate;
        const endDate = this.endDate;
        const dates: string[] = [];

        let d = startDate;
        while (d <= endDate) {
            dates.push(new Intl.DateTimeFormat(this.language, {
                weekday: 'short',
            }).format(d.toJSDate()))

            d = d.plus({ days: 1 });
        }

        return dates
    }

    getMonthlyRange(): string[] {
        const startDate = this.startDate;
        const endDate = this.endDate;
        const dates: string[] = [];

        let d = startDate;
        while (d <= endDate) {
            dates.push(new Intl.DateTimeFormat(this.language, {
                month: 'short',
                day: '2-digit',
            }).format(d.toJSDate()))

            d = d.plus({ days: 1 });
        }

        return dates
    }
    getYearlyRange(): string[] {
        const startDate = this.startDate;
        const endDate = this.endDate;
        const dates: string[] = [];

        let d = startDate;
        while (d <= endDate) {
            dates.push(new Intl.DateTimeFormat(this.language, {
                month: 'short',
                year: 'numeric',
            }).format(d.toJSDate()))

            d = d.plus({ month: 1 });
        }

        return dates
    }

    getCustomRange(): string[] {
        const start = this.startDate;
        const end = this.endDate;
        const diffDays = end.diff(start, 'days').days;

        // If less than 90 days, show daily range
        if (diffDays < 90) {
            const dates: string[] = [];
            let d = start;
            while (d <= end) {
                dates.push(new Intl.DateTimeFormat(this.language, {
                    month: 'short',
                    day: '2-digit',
                }).format(d.toJSDate()));
                d = d.plus({ days: 1 });
            }
            return dates;
        }

        // Otherwise show monthly range
        const dates: string[] = [];
        let d = start;
        while (d <= end) {
            dates.push(new Intl.DateTimeFormat(this.language, {
                month: 'short',
                year: 'numeric',
            }).format(d.toJSDate()));
            d = d.plus({ month: 1 });
        }
        return dates;
    }

    formatBoundaries(): string {
        switch (this._reportType) {
            case 'daily':
                return this.formatDailyBoundaries(this.startDate);
            case 'weekly':
                return this.formatWeeklyBoundaries(this.startDate, this.endDate);
            case 'monthly':
                return this.formatMonthlyBoundaries(this.startDate);
            case 'yearly':
                return this.formatYearlyBoundaries(this.startDate);
            case 'custom':
                return this.formatCustomBoundaries();
            default:
                return this.formatDailyBoundaries(this.startDate);
        }
    }

    formatDailyBoundaries(start: DateTime = this.startDate): string {
        return new Intl.DateTimeFormat(this.language, {
            month: 'short',
            day: '2-digit',
        }).format(start.toJSDate());
    }


    formatWeeklyBoundaries(start: DateTime = this.startDate, end: DateTime = this.endDate): string {
        let startDate = new Intl.DateTimeFormat(this.language, {
            month: 'short',
            day: '2-digit',
        }).format(start.toJSDate());

        let endDate = new Intl.DateTimeFormat(this.language, {
            month: 'short',
            day: '2-digit',
        }).format(end.toJSDate());

        return startDate + ' - ' + endDate;
    }

    formatMonthlyBoundaries(start: DateTime = this.startDate): string {
        let startDate = new Intl.DateTimeFormat(this.language, {
            month: 'long',
            year: 'numeric'
        }).format(start.toJSDate());

        // capitalize the first letter of the month
        return startDate.charAt(0).toUpperCase() + startDate.slice(1);
    }
    formatYearlyBoundaries(start: DateTime = this.startDate): string {
        let startDate = new Intl.DateTimeFormat(this.language, {
            year: 'numeric',
        }).format(start.toJSDate());


        return startDate;
    }

    formatCustomBoundaries(): string {
        const start = new Intl.DateTimeFormat(this.language, {
            month: 'short',
            day: '2-digit',
        }).format(this.startDate.toJSDate());

        const end = new Intl.DateTimeFormat(this.language, {
            month: 'short',
            day: '2-digit',
        }).format(this.endDate.toJSDate());

        return `${start} — ${end}`;
    }
}